import { useState, useEffect, useMemo } from 'react';
import * as Sentry from "@sentry/react";
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { syncNormalizedTags } from '../lib/tags';
import { ASSET_TYPE, INSIGHT_STATUS, INSIGHT_TYPE, IS_MOCK_DEMO } from '../constants';
import { isOfflineError } from '../lib/firestore';
import { mockInsights } from '../mockData';
import { inferInsightKind, isHintInsight, isProposedChangeInsight, normalizeAssetType } from '../lib/utils';

export function useInsights(user, globalSelectedTags = []) {
  const [insights, setInsights] = useState(() => (IS_MOCK_DEMO ? mockInsights.map(i => ({
    ...i,
    title: i.title ?? '',
    summary: i.summary ?? '',
    kind: i.kind ?? inferInsightKind(i),
    tags: i.tags ?? [],
    status: i.status ?? INSIGHT_STATUS.SUGGESTED,
    sourceNoteIds: i.sourceNoteIds ?? [],
    sourceRoomIds: i.sourceRoomIds ?? [],
  })) : []));
  const [insightSearchText, setInsightSearchText] = useState('');
  const [selectedInsightStatus, setSelectedInsightStatus] = useState(INSIGHT_STATUS.SUGGESTED);
  const [selectedInsightSourceNoteId, setSelectedInsightSourceNoteId] = useState(null);
  const [selectedInsightSourceIds, setSelectedInsightSourceIds] = useState([]);
  const [selectedInsightType, setSelectedInsightType] = useState('all'); // 'all', 'hint', 'duplicate', 'proposal_actionable', 'tag_merge'
  const [selectedInsightId, setSelectedInsightId] = useState(null);
  const [selectedInsightKind, setSelectedInsightKind] = useState('all'); // 'all', 'hint', 'proposed_change'

  // Rejection State
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectingInsightId, setRejectingInsightId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Promotion State
  const [isAssetTypeModalOpen, setIsAssetTypeModalOpen] = useState(false);
  const [assetizingInsight, setAssetizingInsight] = useState(null);
  const [assetTypeDraft, setAssetTypeDraft] = useState(ASSET_TYPE.KNOWLEDGE);
  const [assetTitleDraft, setAssetTitleDraft] = useState('');
  const [assetBodyDraft, setAssetBodyDraft] = useState('');

  useEffect(() => {
    if (IS_MOCK_DEMO || !user?.uid) return undefined;
    const insightsQuery = query(
      collection(db, 'insights'),
      where('ownerUid', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(insightsQuery, (snapshot) => {
      setInsights(snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          kind: data.kind || inferInsightKind(data),
          title: data.title ?? '',
          summary: data.summary ?? '',
          tags: data.tags ?? [],
          status: data.status ?? INSIGHT_STATUS.SUGGESTED,
          sourceNoteIds: data.sourceNoteIds ?? [],
          sourceRoomIds: data.sourceRoomIds ?? [],
        };
      }));
    }, (error) => {
      console.error('Failed to subscribe insights:', error);
      if (!isOfflineError(error)) {
        Sentry.captureException(error, { tags: { action: 'useInsights_onSnapshot' } });
        alert('Hintsの同期に失敗しました。Firestoreのルールを確認してください。');
      }
    });
  }, [user?.uid]);

  const selectedInsight = useMemo(() => (
    insights.find((insight) => insight.id === selectedInsightId) || null
  ), [insights, selectedInsightId]);

  const pendingHintCount = useMemo(() => (
    insights.filter(i => i.status === INSIGHT_STATUS.SUGGESTED && isHintInsight(i)).length
  ), [insights]);

  const filteredInsights = useMemo(() => (
    insights.filter((insight) => {
      const status = insight.status || INSIGHT_STATUS.SUGGESTED;
      const matchesStatus = selectedInsightStatus === 'all' || status === selectedInsightStatus;
      const matchesTag = globalSelectedTags.length === 0 || globalSelectedTags.some((tag) => (insight.tags || []).includes(tag));
      const matchesNote = !selectedInsightSourceNoteId || (insight.sourceNoteIds || []).includes(selectedInsightSourceNoteId);
      const matchesSourceIds = selectedInsightSourceIds.length === 0 || selectedInsightSourceIds.includes(insight.id);

      const matchesKind = selectedInsightKind === 'all' || insight.kind === selectedInsightKind;

      const type = insight.type || '';
      let matchesType = selectedInsightType === 'all' && type !== INSIGHT_TYPE.TAG_MERGE;
      if (!matchesType) {
        if (selectedInsightType === 'duplicate') {
          matchesType = type.startsWith('duplicate');
        } else if (selectedInsightType === 'proposal') {
          matchesType = isProposedChangeInsight(insight);
        } else if (selectedInsightType === 'proposal_actionable') {
          matchesType = isProposedChangeInsight(insight) &&
            type !== INSIGHT_TYPE.MISSING_LINK &&
            type !== INSIGHT_TYPE.TAG_MERGE;
        } else if (selectedInsightType === 'tag_merge') {
          matchesType = type === INSIGHT_TYPE.TAG_MERGE;
        } else if (selectedInsightType === 'missing_link') {
          matchesType = type === 'missing_link';
        } else if (selectedInsightType === 'hint') {
          matchesType = isHintInsight(insight);
        }
      }

      const searchText = insightSearchText.toLowerCase();
      const matchesSearch = !searchText ||
        (insight.title || '').toLowerCase().includes(searchText) ||
        (insight.summary || '').toLowerCase().includes(searchText);

      return matchesStatus && matchesTag && matchesNote && matchesSourceIds && matchesKind && matchesType && matchesSearch;
    })
  ), [insights, globalSelectedTags, selectedInsightStatus, selectedInsightSourceNoteId, selectedInsightSourceIds, selectedInsightKind, selectedInsightType, insightSearchText]);

  const handleUpdateInsightTags = async (insightId, tags) => {
    const previousTags = insights.find(i => i.id === insightId)?.tags || [];
    if (IS_MOCK_DEMO) {
      setInsights((current) => current.map((insight) => (
        insight.id === insightId ? { ...insight, tags, updatedAt: new Date().toISOString() } : insight
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'insights', insightId), { tags, updatedAt: serverTimestamp() });
      await syncNormalizedTags(db, user?.uid, insightId, 'insight', tags, previousTags);
    } catch (error) {
      console.error('Failed to update insight tags:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateInsightTags' }, extra: { insightId } });
      throw error;
    }
  };

  const handleRejectInsight = async (e) => {
    if (e) e.preventDefault();
    if (!rejectingInsightId) return;

    const insightId = rejectingInsightId;
    
    if (IS_MOCK_DEMO) {
      const now = new Date().toISOString();
      setInsights((current) => current.map((insight) => (
        insight.id === insightId
          ? {
            ...insight,
            status: INSIGHT_STATUS.REJECTED,
            updatedAt: now,
            resolvedAt: now,
            rejectionMemo: rejectReason || null,
          }
          : insight
      )));
    } else {
      try {
        await updateDoc(doc(db, 'insights', insightId), {
          status: INSIGHT_STATUS.REJECTED,
          updatedAt: serverTimestamp(),
          resolvedAt: serverTimestamp(),
          rejectionMemo: rejectReason || null,
        });
      } catch (error) {
        console.error('Failed to reject insight:', error);
        Sentry.captureException(error, { tags: { action: 'handleRejectInsight' }, extra: { insightId } });
        alert('Hintの却下に失敗しました。');
        return;
      }
    }

    setIsRejectModalOpen(false);
    setRejectingInsightId(null);
    setRejectReason('');
    setSelectedInsightId(null);
  };

  const handleUpdateInsightTitle = async (insightId, title) => {
    if (IS_MOCK_DEMO) {
      setInsights((current) => current.map((insight) => (
        insight.id === insightId ? { ...insight, title, updatedAt: new Date().toISOString() } : insight
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'insights', insightId), { title, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Failed to update insight title:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateInsightTitle' }, extra: { insightId } });
      throw error;
    }
  };

  const handleUpdateInsightSummary = async (insightId, summary) => {
    if (IS_MOCK_DEMO) {
      setInsights((current) => current.map((insight) => (
        insight.id === insightId ? { ...insight, summary, updatedAt: new Date().toISOString() } : insight
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'insights', insightId), { summary, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Failed to update insight summary:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateInsightSummary' }, extra: { insightId } });
      throw error;
    }
  };

  const handleUpdateInsightMemo = async (insightId, rejectionMemo) => {
    if (IS_MOCK_DEMO) {
      setInsights((current) => current.map((insight) => (
        insight.id === insightId ? { ...insight, rejectionMemo, updatedAt: new Date().toISOString() } : insight
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'insights', insightId), { rejectionMemo, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Failed to update insight memo:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateInsightMemo' }, extra: { insightId } });
    }
  };

  const handleUpdateInsightPatch = async (insightId, proposedPatch) => {
    if (IS_MOCK_DEMO) {
      setInsights((current) => current.map((insight) => (
        insight.id === insightId ? { ...insight, proposedPatch, updatedAt: new Date().toISOString() } : insight
      )));
      return;
    }
    try {
      await updateDoc(doc(db, 'insights', insightId), { proposedPatch, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error('Failed to update insight patch:', error);
      Sentry.captureException(error, { tags: { action: 'handleUpdateInsightPatch' }, extra: { insightId } });
      throw error;
    }
  };

  const openAssetTypeModal = (insight) => {
    setAssetizingInsight(insight);
    setAssetTypeDraft(normalizeAssetType(insight.suggestedTargetType));
    setAssetTitleDraft(insight.title || '');
    setAssetBodyDraft(insight.summary || '');
    setIsAssetTypeModalOpen(true);
  };

  return {
    insights,
    setInsights,
    insightSearchText,
    setInsightSearchText,
    selectedInsightStatus,
    setSelectedInsightStatus,
    selectedInsightSourceNoteId,
    setSelectedInsightSourceNoteId,
    selectedInsightSourceIds,
    setSelectedInsightSourceIds,
    selectedInsightKind,
    setSelectedInsightKind,
    selectedInsightType,
    setSelectedInsightType,
    selectedInsightId,
    setSelectedInsightId,
    selectedInsight,
    pendingHintCount,
    filteredInsights,
    isRejectModalOpen,
    setIsRejectModalOpen,
    rejectingInsightId,
    setRejectingInsightId,
    rejectReason,
    setRejectReason,
    isAssetTypeModalOpen,
    setIsAssetTypeModalOpen,
    assetizingInsight,
    setAssetizingInsight,
    assetTypeDraft,
    setAssetTypeDraft,
    assetTitleDraft,
    setAssetTitleDraft,
    assetBodyDraft,
    setAssetBodyDraft,
    handleUpdateInsightTags,
    handleUpdateInsightTitle,
    handleUpdateInsightSummary,
    handleRejectInsight,
    handleUpdateInsightMemo,
    handleUpdateInsightPatch,
    openAssetTypeModal,
  };
}
